/**
 * Sale totals, round-off and payment settlement.
 *
 * A completed invoice is immutable: the totals are computed once, at sale time, from the
 * priced lines and stored on the invoice. Returns never recompute a sale from scratch —
 * they reverse the stored per-line amounts pro-rata, which is why every line keeps its
 * tax components.
 */

import type { Milli, Paise, RoundOffMode } from './money.js';
import { applyRoundOff, multiply } from './money.js';
import { isOpenItem } from './open-item.js';
import type { PricedLine } from './pricing.js';
import type { TaxBucket, TaxComponents } from './tax.js';
import { ZERO_COMPONENTS, addComponents, bucketsToRows } from './tax.js';

export interface SaleTotals {
  lineCount: number;
  distinctProducts: number;
  totalQtyMilli: Milli;
  /** sum of (rate x qty) before any discount */
  grossPaise: Paise;
  itemDiscountPaise: Paise;
  billDiscountPaise: Paise;
  totalDiscountPaise: Paise;
  /** value tax is charged on */
  taxablePaise: Paise;
  tax: TaxComponents;
  /** taxable + tax, before round-off */
  subTotalPaise: Paise;
  roundOffPaise: Paise;
  /** what the customer actually pays */
  grandTotalPaise: Paise;
  taxBreakup: TaxBucket[];
}

export function computeTotals(lines: PricedLine[], buckets: Map<string, TaxBucket>, roundOffMode: RoundOffMode): SaleTotals {
  if (lines.length === 0) {
    return {
      lineCount: 0,
      distinctProducts: 0,
      totalQtyMilli: 0,
      grossPaise: 0,
      itemDiscountPaise: 0,
      billDiscountPaise: 0,
      totalDiscountPaise: 0,
      taxablePaise: 0,
      tax: ZERO_COMPONENTS,
      subTotalPaise: 0,
      roundOffPaise: 0,
      grandTotalPaise: 0,
      taxBreakup: [],
    };
  }

  const grossPaise = lines.reduce((sum, line) => sum + line.grossPaise, 0);
  const itemDiscountPaise = lines.reduce((sum, line) => sum + line.itemDiscountPaise, 0);
  const billDiscountPaise = lines.reduce((sum, line) => sum + line.billDiscountPaise, 0);

  const tax = lines.reduce<TaxComponents>(
    (accumulator, line) => addComponents(accumulator, line.tax),
    { ...ZERO_COMPONENTS },
  );
  // The line taxable values already have the bill discount deducted; the summed tax
  // components are the authority so we take the taxable total from them.
  const taxablePaise = tax.taxablePaise;
  const subTotalPaise = taxablePaise + tax.gstPaise + tax.cessPaise;
  const { payable, roundOff } = applyRoundOff(subTotalPaise, roundOffMode);

  return {
    lineCount: lines.length,
    // Open items share one system product id, so count them per line, not per product.
    distinctProducts: new Set(lines.map((line) => (isOpenItem(line.productId) ? line.lineId : line.productId))).size,
    // Mixed UOMs (kg + pcs) have no meaningful sum; kept for single-UOM bills only.
    totalQtyMilli: lines.reduce((sum, line) => sum + line.qtyMilli, 0),
    grossPaise,
    itemDiscountPaise,
    billDiscountPaise,
    totalDiscountPaise: itemDiscountPaise + billDiscountPaise,
    taxablePaise,
    tax,
    subTotalPaise,
    roundOffPaise: roundOff,
    grandTotalPaise: payable,
    taxBreakup: bucketsToRows(buckets),
  };
}

/** Amount still to be collected on a partially paid invoice (0 once fully paid). Never negative. */
export function outstanding(grandTotalPaise: Paise, paidPaise: Paise): Paise {
  return Math.max(0, grandTotalPaise - paidPaise);
}

/** Cost / margin figures for the profit report. Stored per line at sale time. */
export interface MarginLine {
  qtyMilli: Milli;
  costPaise: Paise;
  netRevenuePaise: Paise;
}

export function marginForLine(line: PricedLine, costPaisePerUnit: Paise) {
  const cost = multiply(costPaisePerUnit, line.qtyMilli);
  return {
    costPaise: cost,
    // Revenue excludes tax — tax is collected on behalf of the government.
    netRevenuePaise: line.taxablePaise,
    grossProfitPaise: line.taxablePaise - cost,
  };
}
